import { MathUtils, Vector3 } from 'three';
import { useEffect, useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import { $sceneBindings } from '../../state/appState';
import type { SceneControls } from '../../scene/controls/SceneControls';
import type { OrbitCameraControls } from '../../scene/controls/OrbitCameraControls';
import styles from './RestrictedCameraTuner.module.css';

/**
 * Dev-overlay для подбора параметров restricted-camera (top-down) сцены.
 * Активируется URL-флагом `?dev`. Виден только когда активная сцена
 * использует `panMode: 'groundPlane'` (определяется по полю `orbit.panMode`);
 * иначе компонент возвращает `null`.
 *
 * Все слайдеры применяются live через мутацию полей `controls.orbit.*` +
 * `applyLimits()` / `refreshPanMode()`. Кнопка «Скопировать config»
 * формирует готовый блок `controls: { … }` для вставки в SceneConfig.
 */

type Snapshot = {
  tiltFromHorizonDeg: number; // 90° - polar
  azimuthDeg: number;
  distance: number;
  minDistance: number;
  maxDistance: number;
  panBoundsXZ: { minX: number; maxX: number; minZ: number; maxZ: number };
  panSpeed: number;
  inertiaFactor: number;
  zoomSpeed: number;
  focus: { x: number; z: number };
  cameraPos: { x: number; y: number; z: number };
  targetPos: { x: number; y: number; z: number };
};

/**
 * Snapshot of all lock-related orbit fields, captured before entering
 * free-fly mode. On lock-restore we copy these values back so the tuner
 * leaves the scene exactly as it was.
 */
type LockSnapshot = {
  minPolarAngle: number;
  maxPolarAngle: number;
  minAzimuthAngle: number;
  maxAzimuthAngle: number;
  minDistance: number;
  maxDistance: number;
  panBoundsXZ: OrbitCameraControls['panBoundsXZ'];
  panMode: OrbitCameraControls['panMode'];
};

const isRestrictedCameraScene = (controls: unknown): controls is SceneControls => {
  if (controls == null || typeof controls !== 'object') return false;
  const c = controls as { orbit?: { panMode?: unknown } };
  return c.orbit?.panMode === 'groundPlane';
};

type OrbitCameraInternals = {
  controls: { object: { position: Vector3 } };
};

const cameraDistance = (orbit: OrbitCameraControls, scratch: Vector3): number => {
  const camera = (orbit as unknown as OrbitCameraInternals).controls.object;
  return scratch.copy(camera.position).distanceTo(orbit.target);
};

const computeAzimuth = (orbit: OrbitCameraControls): number => {
  // Three.js OrbitControls measures azimuth around +Y starting at +Z, so
  // azimuth = atan2(offsetX, offsetZ) of the camera offset from target.
  const camera = (orbit as unknown as OrbitCameraInternals).controls.object;
  const offsetX = camera.position.x - orbit.target.x;
  const offsetZ = camera.position.z - orbit.target.z;
  return Math.atan2(offsetX, offsetZ);
};

const computePolar = (orbit: OrbitCameraControls, distance: number): number => {
  const camera = (orbit as unknown as OrbitCameraInternals).controls.object;
  const dy = camera.position.y - orbit.target.y;
  if (distance < 1e-6) return Math.PI / 2;
  return Math.acos(MathUtils.clamp(dy / distance, -1, 1));
};

const buildSnapshot = (orbit: OrbitCameraControls, scratch: Vector3): Snapshot => {
  const distance = cameraDistance(orbit, scratch);
  const azimuth = computeAzimuth(orbit);
  const polar = computePolar(orbit, distance);
  const tiltFromHorizon = Math.PI / 2 - polar;
  const camera = (orbit as unknown as OrbitCameraInternals).controls.object;
  return {
    tiltFromHorizonDeg: MathUtils.radToDeg(tiltFromHorizon),
    azimuthDeg: MathUtils.radToDeg(azimuth),
    distance,
    minDistance: orbit.minDistance,
    maxDistance: orbit.maxDistance,
    panBoundsXZ: orbit.panBoundsXZ ?? {
      minX: -1, maxX: 1, minZ: -1, maxZ: 1,
    },
    panSpeed: orbit.panSpeed,
    inertiaFactor: orbit.inertiaFactor,
    zoomSpeed: orbit.zoomSpeed,
    focus: { x: orbit.target.x, z: orbit.target.z },
    cameraPos: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
    targetPos: { x: orbit.target.x, y: orbit.target.y, z: orbit.target.z },
  };
};

const lockPolar = (orbit: OrbitCameraControls, polarRad: number): void => {
  orbit.minPolarAngle = polarRad;
  orbit.maxPolarAngle = polarRad;
  orbit.applyLimits();
};

const lockAzimuth = (orbit: OrbitCameraControls, azimuthRad: number): void => {
  orbit.minAzimuthAngle = azimuthRad;
  orbit.maxAzimuthAngle = azimuthRad;
  orbit.applyLimits();
};

const NumberField = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
}) => (
  <label className={styles.field}>
    <span className={styles.label}>
      {label}
      <span className={styles.value}>{value.toFixed(step < 1 ? 3 : 0)}</span>
    </span>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className={styles.slider}
    />
  </label>
);

export const RestrictedCameraTuner = () => {
  const bindings = useUnit($sceneBindings);
  const controls = bindings?.controls;
  const isRestricted = isRestrictedCameraScene(controls);
  const sceneControls = isRestricted ? (controls as SceneControls) : null;
  const orbit = sceneControls?.orbit ?? null;
  const scratchRef = useRef<Vector3>(new Vector3());
  const [snapshot, setSnapshot] = useState<Snapshot | null>(
    () => (orbit ? buildSnapshot(orbit, scratchRef.current) : null),
  );
  const [copied, setCopied] = useState<'config' | 'waypoint' | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const lockSnapshotRef = useRef<LockSnapshot | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!orbit || !sceneControls) {
      setSnapshot(null);
      return;
    }
    // Freeze config writes for orbit pose / pan / zoom fields. Without
    // this, every reactive `$appConfig.watch` fire (~from Leva or other
    // dev tools) would re-merge scene-level controls overrides on top
    // of `orbit.*` and visually clobber any slider drag / free-fly
    // mutation. Frozen state is restored only on scene unmount.
    sceneControls.freezeOrbitConfigWrites();
    setSnapshot(buildSnapshot(orbit, scratchRef.current));
    const tick = () => {
      setSnapshot(buildSnapshot(orbit, scratchRef.current));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [orbit, sceneControls]);

  if (!orbit || !snapshot) {
    return null;
  }

  const writeToClipboard = async (text: string, kind: 'config' | 'waypoint'): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1200);
    } catch {
      window.prompt('Скопируй вручную:', text);
    }
  };

  const handleCopy = async () => {
    const s = buildSnapshot(orbit, scratchRef.current);
    const polarRad = Math.PI / 2 - MathUtils.degToRad(s.tiltFromHorizonDeg);
    const azimuthRad = MathUtils.degToRad(s.azimuthDeg);
    const cfg = `controls: {
  orbitMinPolarAngle: ${polarRad.toFixed(5)},
  orbitMaxPolarAngle: ${polarRad.toFixed(5)},
  orbitMinAzimuthAngle: ${azimuthRad.toFixed(5)},
  orbitMaxAzimuthAngle: ${azimuthRad.toFixed(5)},
  orbitMinDistance: ${s.minDistance.toFixed(3)},
  orbitMaxDistance: ${s.maxDistance.toFixed(3)},
  orbitCenter: { x: ${s.focus.x.toFixed(3)}, y: 0, z: ${s.focus.z.toFixed(3)} },
  panMode: 'groundPlane',
  groundPlaneY: 0,
  panBoundsXZ: {
    minX: ${s.panBoundsXZ.minX.toFixed(3)}, maxX: ${s.panBoundsXZ.maxX.toFixed(3)},
    minZ: ${s.panBoundsXZ.minZ.toFixed(3)}, maxZ: ${s.panBoundsXZ.maxZ.toFixed(3)},
  },
  forcePanOnTouch: true,
  panSpeed: ${s.panSpeed.toFixed(3)},
  inertiaFactor: ${s.inertiaFactor.toFixed(3)},
  zoomSpeed: ${s.zoomSpeed.toFixed(2)},
},`;
    await writeToClipboard(cfg, 'config');
  };

  /**
   * Output current camera pose in formats useful for preloader animation
   * authoring:
   *
   * 1. `previewTourStart` / `previewTourEnd` blocks (for `SceneConfig.camera`).
   * 2. A `flyover` waypoint segment (drop into `camera.flyover.segments`).
   *
   * All three live as a single clipboard payload so the user can grab the
   * pose once, then split the parts they need.
   */
  const handleCopyWaypoint = async () => {
    const s = buildSnapshot(orbit, scratchRef.current);
    const fmt = (n: number): string => n.toFixed(3);
    const cfg = `// === preview tour blocks ===
previewTourStart: { x: ${fmt(s.cameraPos.x)}, y: ${fmt(s.cameraPos.y)}, z: ${fmt(s.cameraPos.z)} },
previewTourEnd: {
  position: { x: ${fmt(s.cameraPos.x)}, y: ${fmt(s.cameraPos.y)}, z: ${fmt(s.cameraPos.z)} },
  target:   { x: ${fmt(s.targetPos.x)}, y: ${fmt(s.targetPos.y)}, z: ${fmt(s.targetPos.z)} },
},
// === flyover waypoint segment ===
{
  type: 'waypoint',
  position: { x: ${fmt(s.cameraPos.x)}, y: ${fmt(s.cameraPos.y)}, z: ${fmt(s.cameraPos.z)} },
  target:   { x: ${fmt(s.targetPos.x)}, y: ${fmt(s.targetPos.y)}, z: ${fmt(s.targetPos.z)} },
  duration: 1.5,
},`;
    await writeToClipboard(cfg, 'waypoint');
  };

  /**
   * Free-fly toggle: temporarily disables ALL camera-pose locks so the dev
   * can rotate / pan / zoom freely to find good camera positions for
   * preloader animation segments.
   *
   * Snapshot taken before entering free-fly is restored on toggle-off.
   *
   * Free-fly state mutations:
   *   - polar/azimuth limits → opened to full sphere
   *   - panBoundsXZ → cleared
   *   - panMode → 'screenSpace' (so left-mouse drag rotates instead of
   *     panning the locked-axis ground-plane)
   *
   * Note: this only affects user-input gating. PreviewTour and
   * CameraFlyover already bypass these limits at runtime because they
   * disable controls (`controls.enabled = false`) and drive the camera
   * directly via `cameraDirector.applyPose()` — which is why constraints
   * don't fire during the preloader animation.
   */
  const handleToggleUnlock = (): void => {
    if (!unlocked) {
      // Collapse the preloader cinematic (preview-tour + flyover) right now
      // so the user can free-fly even if scene loading hasn't finished.
      // Without this, the previewInputBlocker DOM overlay keeps swallowing
      // pointer/wheel events and the running transitionToPose keeps writing
      // camera position every frame. Idempotent — late-call is no-op.
      bindings?.forceFinishPreloader?.();
      lockSnapshotRef.current = {
        minPolarAngle: orbit.minPolarAngle,
        maxPolarAngle: orbit.maxPolarAngle,
        minAzimuthAngle: orbit.minAzimuthAngle,
        maxAzimuthAngle: orbit.maxAzimuthAngle,
        minDistance: orbit.minDistance,
        maxDistance: orbit.maxDistance,
        panBoundsXZ: orbit.panBoundsXZ,
        panMode: orbit.panMode,
      };
      orbit.minPolarAngle = 0;
      orbit.maxPolarAngle = Math.PI;
      orbit.minAzimuthAngle = -Infinity;
      orbit.maxAzimuthAngle = Infinity;
      // Open distance range too — overall ships with `orbitMinDistance: 3.5,
      // orbitMaxDistance: 7.0` and the start pose sits at distance ≈ 7,
      // pinned to the upper cap. Without opening these the wheel zooms in
      // but never out. Tuner-frozen distance (`_orbitConfigFrozen`) survives
      // reactive config updates.
      orbit.minDistance = 0;
      orbit.maxDistance = Infinity;
      orbit.panBoundsXZ = null;
      orbit.panMode = 'screenSpace';
      orbit.applyLimits();
      orbit.refreshPanMode();
      setUnlocked(true);
    } else {
      const snap = lockSnapshotRef.current;
      if (snap) {
        orbit.minPolarAngle = snap.minPolarAngle;
        orbit.maxPolarAngle = snap.maxPolarAngle;
        orbit.minAzimuthAngle = snap.minAzimuthAngle;
        orbit.maxAzimuthAngle = snap.maxAzimuthAngle;
        orbit.minDistance = snap.minDistance;
        orbit.maxDistance = snap.maxDistance;
        orbit.panBoundsXZ = snap.panBoundsXZ;
        orbit.panMode = snap.panMode;
        orbit.applyLimits();
        orbit.refreshPanMode();
      }
      lockSnapshotRef.current = null;
      setUnlocked(false);
    }
  };

  return (
    <div className={styles.overlay} dir="ltr">
      <div className={styles.header}>
        <div className={styles.title}>Restricted-camera tuner</div>
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className={styles.collapseButton}
          aria-label={collapsed ? 'Развернуть' : 'Свернуть'}
          title={collapsed ? 'Развернуть' : 'Свернуть'}
        >
          {collapsed ? '▾' : '▴'}
        </button>
      </div>

      {!collapsed && (
        <>
      <NumberField
        label="tiltFromHorizonDeg"
        value={snapshot.tiltFromHorizonDeg}
        min={10}
        max={89}
        step={0.5}
        onChange={(v) =>
          lockPolar(
            orbit,
            Math.PI / 2 - MathUtils.degToRad(MathUtils.clamp(v, 10, 89)),
          )
        }
      />

      <NumberField
        label="azimuthDeg"
        value={snapshot.azimuthDeg}
        min={-180}
        max={180}
        step={1}
        onChange={(v) => lockAzimuth(orbit, MathUtils.degToRad(v))}
      />

      <NumberField
        label="minDistance"
        value={snapshot.minDistance}
        min={0.1}
        max={10}
        step={0.05}
        onChange={(v) => {
          orbit.minDistance = Math.max(v, 1e-4);
          if (orbit.maxDistance < orbit.minDistance) {
            orbit.maxDistance = orbit.minDistance;
          }
          orbit.applyLimits();
        }}
      />
      <NumberField
        label="maxDistance"
        value={snapshot.maxDistance}
        min={0.1}
        max={20}
        step={0.05}
        onChange={(v) => {
          orbit.maxDistance = Math.max(v, orbit.minDistance);
          orbit.applyLimits();
        }}
      />

      <div className={styles.section}>panBoundsXZ</div>
      <NumberField
        label="minX"
        value={snapshot.panBoundsXZ.minX}
        min={-10}
        max={10}
        step={0.05}
        onChange={(v) => {
          orbit.panBoundsXZ = { ...snapshot.panBoundsXZ, minX: v };
        }}
      />
      <NumberField
        label="maxX"
        value={snapshot.panBoundsXZ.maxX}
        min={-10}
        max={10}
        step={0.05}
        onChange={(v) => {
          orbit.panBoundsXZ = { ...snapshot.panBoundsXZ, maxX: v };
        }}
      />
      <NumberField
        label="minZ"
        value={snapshot.panBoundsXZ.minZ}
        min={-10}
        max={10}
        step={0.05}
        onChange={(v) => {
          orbit.panBoundsXZ = { ...snapshot.panBoundsXZ, minZ: v };
        }}
      />
      <NumberField
        label="maxZ"
        value={snapshot.panBoundsXZ.maxZ}
        min={-10}
        max={10}
        step={0.05}
        onChange={(v) => {
          orbit.panBoundsXZ = { ...snapshot.panBoundsXZ, maxZ: v };
        }}
      />

      <div className={styles.section}>feel</div>
      <NumberField
        label="panSpeed"
        value={snapshot.panSpeed}
        min={0.1}
        max={2}
        step={0.05}
        onChange={(v) => {
          orbit.panSpeed = v;
        }}
      />
      <NumberField
        label="inertiaFactor"
        value={snapshot.inertiaFactor}
        min={0}
        max={0.99}
        step={0.01}
        onChange={(v) => {
          orbit.inertiaFactor = MathUtils.clamp(v, 0, 0.99);
        }}
      />
      <NumberField
        label="zoomSpeed"
        value={snapshot.zoomSpeed}
        min={0.1}
        max={10}
        step={0.1}
        onChange={(v) => {
          orbit.zoomSpeed = v;
          orbit.applyLimits();
        }}
      />

      <div className={styles.readout}>
        <div>distance: {snapshot.distance.toFixed(3)}</div>
        <div>
          focus: ({snapshot.focus.x.toFixed(3)}, {snapshot.focus.z.toFixed(3)})
        </div>
      </div>

      <div className={styles.section}>camera pose</div>
      <div className={styles.readout}>
        <div>
          camera: ({snapshot.cameraPos.x.toFixed(3)}, {snapshot.cameraPos.y.toFixed(3)},{' '}
          {snapshot.cameraPos.z.toFixed(3)})
        </div>
        <div>
          target: ({snapshot.targetPos.x.toFixed(3)}, {snapshot.targetPos.y.toFixed(3)},{' '}
          {snapshot.targetPos.z.toFixed(3)})
        </div>
      </div>

      <button
        type="button"
        onClick={handleToggleUnlock}
        className={`${styles.unlock} ${unlocked ? styles.unlockActive : ''}`}
      >
        {unlocked ? '🔒 Lock camera (restore restrictions)' : '🔓 Unlock camera (free-fly)'}
      </button>
      {unlocked && (
        <div className={styles.unlockHint}>
          Free-fly: locks отключены (polar/azimuth/cage). Двигай мышью/тачем,
          ищи кадр, копируй waypoint. Нажми «Lock» чтобы вернуть оригинальные
          ограничения.
        </div>
      )}

      <button type="button" onClick={handleCopyWaypoint} className={styles.copy}>
        {copied === 'waypoint' ? 'Скопировано!' : 'Скопировать waypoint'}
      </button>

      <button type="button" onClick={handleCopy} className={styles.copy}>
        {copied === 'config' ? 'Скопировано!' : 'Скопировать controls config'}
      </button>
        </>
      )}
    </div>
  );
};

RestrictedCameraTuner.displayName = 'RestrictedCameraTuner';
